import { ProjectsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import type { Project, ProjectId } from "@storytell/types";
import { createResource } from "solid-js";

export type ProjectsService = ReturnType<typeof createProjectsService>;

export const createProjectsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
}) => {
  const client = useStorytellClient();

  const [projectsData, { refetch, mutate }] = createResource(
    () => deps.authReady() && deps.userId(),
    async (): Promise<Record<ProjectId, Project>> => {
      const request = ProjectsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch projects sync", response.error);
        return {};
      }
      return response.value.projects;
    },
    {
      initialValue: {},
      deferStream: true,
    },
  );

  // This is used to trigger a suspense boundary so SSR works properly
  // Don't use it for data (that's why it's returned as undefined)
  const suspenseTrigger = () => (projectsData() ? undefined : undefined);

  const all = () => Object.values(projectsData.latest);
  const get = (id: ProjectId): Project | undefined => projectsData.latest?.[id];
  const set = (id: ProjectId, project: Project) => mutate((prev) => ({ ...prev, [id]: project }));

  const canDelete = (id: ProjectId) => (get(id)?.grants?.["op.delete"] && !get(id)?.default) ?? false;
  const canUpdate = (id: ProjectId) => get(id)?.grants?.["op.update"] ?? false;
  const canRead = (id: ProjectId) => get(id)?.grants?.["op.read"] ?? false;
  const canCreate = (id: ProjectId) => get(id)?.grants?.["op.create"] ?? false;

  return {
    suspenseTrigger,
    refetch,
    all,
    get,
    set,
    canDelete,
    canUpdate,
    canRead,
    canCreate,
  };
};
